import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { authorizedFetch, queryClient } from '@/lib/queryClient';
import FileUpload from '@/components/upload/file-upload';
import { useNav } from '@/lib/nav';
import routes from '@/lib/routes';
import { Eye, Trash2 } from 'lucide-react';

export default function ProjectDataLibrary({
  projectId,
  files,
  onRefetch,
}: {
  projectId: string;
  files: any[];
  onRefetch: () => void;
}) {
  const [showUpload, setShowUpload] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const { go } = useNav();

  const handleReplace = () => setShowUpload(true);
  const deleteFile = async (fileId: string) => {
    try {
      // Some installations enforce a JSON content-type guard even for DELETE
      const res = await authorizedFetch(`/api/files/${fileId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      if (!res.ok) throw new Error('delete_failed');
      // Invalidate React Query cache for files so lumichat and other components refresh
      try { queryClient.invalidateQueries({ queryKey: ['files'] }); } catch {}
      onRefetch();
    } catch (e) {
      console.error('Failed to delete file', e);
      // Invalidate even on error to ensure cache consistency
      try { queryClient.invalidateQueries({ queryKey: ['files'] }); } catch {}
      onRefetch();
    }
  };
  const handleUploaded = () => {
    setShowUpload(false);
    onRefetch();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Data Library</h2>
        <Button onClick={() => setShowUpload(true)}>Upload</Button>
      </div>
      <div className="overflow-auto border rounded-md">
        <table className="w-full text-sm table-fixed">
          <colgroup>
            <col style={{ width: '50%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '14%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <thead className="bg-muted">
            <tr className="border-b">
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-right">Rows</th>
              <th className="px-3 py-2 text-right">Cols</th>
              <th className="px-3 py-2 text-center">Updated</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f) => (
              <tr key={f.id} className="border-b hover:bg-muted/30">
                <td className="px-3 py-2 truncate">{(f.metadata?.originalName) || f.filename || f.objectPath}</td>
                <td className="px-3 py-2 text-right">{(f.metadata?.rowCount) ?? f.rowCount ?? '-'}</td>
                <td className="px-3 py-2 text-right">{(f.metadata?.columnCount) ?? f.columnCount ?? (Array.isArray(f.metadata?.fingerprint?.columns) ? f.metadata.fingerprint.columns.length : '-')}</td>
                <td className="px-3 py-2 text-center">{new Date(f.createdAt ?? f.uploadedAt ?? Date.now()).toLocaleDateString()}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label="Preview dataset"
                      title="Preview"
                      onClick={() => go(routes.project.file(projectId, String(f.id)))}
                    >
                      <Eye />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      aria-label="Delete dataset"
                      title="Delete"
                      onClick={() => setPendingDelete(f.id)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {files.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-muted-foreground" colSpan={5}>No datasets yet - click Upload to add your first CSV.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showUpload && (
        <FileUpload
          projectId={projectId}
          onClose={() => setShowUpload(false)}
          onFileUploaded={handleUploaded}
        />
      )}

      {/* Delete confirm */}
      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete dataset?</AlertDialogTitle>
            <AlertDialogDescription>
              Deleting this dataset will remove it from your project. Any plots that relied on it may not display properly.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={async () => {
                if (pendingDelete) await deleteFile(pendingDelete);
                setPendingDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
